import { useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Trash2, Sparkles } from "lucide-react";

const TIMING_ROWS = [
  ["MORNING", "AFTERNOON", "EVENING", "NIGHT"],
  ["BEFORE FOOD", "AFTER FOOD"],
  ["SOS (When needed)", "STAT (Immediately)", "BEDTIME"],
];

const DOSES = ["1-0-0", "0-0-1", "1-0-1", "1-1-1", "0-1-0", "1-0-1-1", "0-0-0"];
const DURATIONS = ["3 DAYS", "5 DAYS", "7 DAYS", "10 DAYS", "15 DAYS", "1 MONTH", "SOS"];

// Timing tokens that represent a time-of-day slot (auto-managed by dose picks).
// Everything else in `food` (BEFORE FOOD, SOS, BEDTIME, etc.) is preserved.
const SLOT_TOKENS = new Set(["MORNING", "AFTERNOON", "EVENING", "NIGHT"]);
const SLOT_ORDER_3 = ["MORNING", "AFTERNOON", "NIGHT"];
const SLOT_ORDER_4 = ["MORNING", "AFTERNOON", "EVENING", "NIGHT"];

function slotsFromDose(dose) {
  const parts = String(dose || "").split("-").map((x) => x.trim());
  if (parts.length !== 3 && parts.length !== 4) return [];
  const order = parts.length === 4 ? SLOT_ORDER_4 : SLOT_ORDER_3;
  return parts
    .map((n, i) => (parseInt(n, 10) > 0 ? order[i] : null))
    .filter(Boolean);
}

function applyDoseToFood(food, dose) {
  // Replace the M/A/E/N slot tokens in food with the ones implied by dose.
  const kept = String(food || "")
    .split("+")
    .map((x) => x.trim())
    .filter((x) => x && !SLOT_TOKENS.has(x));
  const slots = slotsFromDose(dose);
  return [...kept, ...slots].join(" + ");
}

/**
 * Single medicine row with catalog autocomplete + clickable dosage timing buttons.
 */
export default function MedicineRow({ value, onChange, onRemove, index, custom, onToggleCustom }) {
  const [q, setQ] = useState(value.name || "");
  const [suggests, setSuggests] = useState([]);
  const [open, setOpen] = useState(false);
  const box = useRef(null);
  const justPicked = useRef(false);

  useEffect(() => { setQ(value.name || ""); }, [value.name]);

  useEffect(() => {
    if (custom) return;
    if (justPicked.current) { justPicked.current = false; return; }
    if (!q || q.length < 2) { setSuggests([]); return; }
    const t = setTimeout(async () => {
      const { data } = await api.get("/medicines", { params: { q } });
      setSuggests(data.slice(0, 8));
      setOpen(true);
    }, 200);
    return () => clearTimeout(t);
  }, [q, custom]);

  useEffect(() => {
    const onOut = (e) => { if (box.current && !box.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onOut);
    return () => document.removeEventListener("mousedown", onOut);
  }, []);

  const pick = (m) => {
    justPicked.current = true;
    onChange({ ...value, name: m.name, generic: m.generic || "", mrp: m.selling_price || 0 });
    setQ(m.name); setOpen(false); setSuggests([]);
  };

  return (
    <div className="border rounded-sm p-3 space-y-2 bg-slate-50/40" data-testid={`med-row-${index}`}>
      <div className="flex items-start gap-2">
        <div className="w-6 text-sm font-semibold text-slate-500 pt-2">{index + 1}</div>
        <div className="flex-1" ref={box}>
          <div className="flex items-center gap-2">
            <Input
              value={q}
              onChange={(e) => { const nv = e.target.value.toUpperCase(); setQ(nv); onChange({ ...value, name: nv }); }}
              placeholder={custom ? "Type custom medicine name" : "Type medicine name (autocomplete from master)"}
              className="h-9 uppercase"
              data-testid={`med-name-${index}`}
              onFocus={() => q.length >= 2 && suggests.length > 0 && setOpen(true)}
            />
            <button type="button" onClick={onToggleCustom} data-testid={`med-toggle-custom-${index}`}
              className={`text-[11px] px-2 h-9 border rounded-sm flex items-center gap-1 ${custom ? "border-amber-500 bg-amber-50 text-amber-800" : "border-slate-300 text-slate-500"}`}>
              <Sparkles className="w-3 h-3"/> {custom ? "Custom" : "+ Custom"}
            </button>
            <button type="button" onClick={onRemove} data-testid={`med-remove-${index}`} className="text-red-600 hover:bg-red-50 p-1.5 rounded-sm"><Trash2 className="w-3.5 h-3.5"/></button>
          </div>
          {open && !custom && suggests.length > 0 && (
            <div className="border bg-white shadow-md z-10 relative -mt-px max-h-60 overflow-y-auto">
              {suggests.map((m) => (
                <div key={m.id} onClick={() => pick(m)} data-testid={`med-suggest-${m.id}`}
                  className="p-2 border-b hover:bg-blue-50 cursor-pointer text-sm flex justify-between items-center">
                  <div>
                    <div className="font-medium uppercase">{m.name}</div>
                    <div className="text-xs text-slate-500">{m.generic} · {m.category} · Stock {m.stock}</div>
                  </div>
                  <div className="font-mono text-xs text-slate-600">₹{m.selling_price}</div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Dose + duration quick picks */}
      <div className="flex flex-wrap gap-2 items-center pl-8">
        <div className="text-[10px] tracking-wider text-slate-500 w-14">DOSE</div>
        {DOSES.map((d) => (
          <button key={d} type="button"
            onClick={() => onChange({ ...value, dose: d, food: applyDoseToFood(value.food, d) })}
            data-testid={`med-dose-${index}-${d}`}
            className={`text-[11px] font-mono px-2 h-6 border rounded-sm ${value.dose === d ? "border-blue-600 bg-blue-50 text-blue-700 font-semibold" : "border-slate-300 text-slate-600"}`}>{d}</button>
        ))}
        <Input value={value.dose || ""}
          onChange={(e) => {
            const nv = e.target.value;
            // Only auto-sync timings when the custom value looks like a X-X-X dose pattern.
            const patch = /^\d(-\d){2,3}$/.test(nv.trim())
              ? { dose: nv, food: applyDoseToFood(value.food, nv) }
              : { dose: nv };
            onChange({ ...value, ...patch });
          }}
          placeholder="Custom" className="h-6 w-24 text-xs" />
      </div>

      {/* Timing quick picks */}
      <div className="pl-8 space-y-1">
        <div className="text-[10px] tracking-wider text-slate-500">TIMING</div>
        {TIMING_ROWS.map((row, r) => (
          <div key={r} className="flex flex-wrap gap-1.5">
            {row.map((t) => {
              const active = (value.food || "").split("+").map((x) => x.trim()).includes(t);
              return (
                <button key={t} type="button"
                  onClick={() => {
                    const parts = new Set((value.food || "").split("+").map((x) => x.trim()).filter(Boolean));
                    const wasActive = parts.has(t);
                    if (wasActive) parts.delete(t); else parts.add(t);
                    const next = { ...value, food: Array.from(parts).join(" + ") };
                    // SOS medicines have no fixed schedule — auto-fill dose as 0-0-0
                    // the moment SOS is selected so the Rx prints cleanly.
                    if (!wasActive && t.startsWith("SOS")) next.dose = "0-0-0";
                    onChange(next);
                  }}
                  data-testid={`med-timing-${index}-${t.split(" ")[0]}`}
                  className={`text-[11px] px-2 h-6 border rounded-sm ${active ? "border-teal-600 bg-teal-50 text-teal-700 font-semibold" : "border-slate-300 text-slate-600"}`}>
                  {t}
                </button>
              );
            })}
          </div>
        ))}
      </div>

      {/* Duration */}
      <div className="flex flex-wrap gap-1.5 items-center pl-8">
        <div className="text-[10px] tracking-wider text-slate-500 w-14">DURATION</div>
        {DURATIONS.map((d) => (
          <button key={d} type="button" onClick={() => onChange({ ...value, duration: d })} data-testid={`med-dur-${index}-${d}`}
            className={`text-[11px] px-2 h-6 border rounded-sm ${value.duration === d ? "border-blue-600 bg-blue-50 text-blue-700 font-semibold" : "border-slate-300 text-slate-600"}`}>{d}</button>
        ))}
        <Input value={value.duration || ""} onChange={(e) => onChange({ ...value, duration: e.target.value })} placeholder="Custom" className="h-6 w-24 text-xs" />
      </div>

      {/* Live Rx preview — mirrors PrescriptionPrint row exactly */}
      <RxPreviewLine value={value} index={index} />
    </div>
  );
}

function RxPreviewLine({ value, index }) {
  const name = (value.name || "").trim();
  const dose = (value.dose || "").trim();
  const food = (value.food || "").trim();
  const duration = (value.duration || "").trim();
  const missing = [];
  if (!name) missing.push("medicine");
  if (!dose) missing.push("dose");
  if (!food) missing.push("timing");
  if (!duration) missing.push("duration");
  const complete = missing.length === 0;
  return (
    <div
      data-testid={`med-preview-${index}`}
      className={`ml-8 border rounded-sm px-3 py-1.5 text-[11px] flex items-start gap-2 ${complete ? "border-emerald-300 bg-emerald-50/60" : "border-amber-300 bg-amber-50/60"}`}
    >
      <span className={`text-[9px] font-semibold tracking-widest uppercase mt-0.5 ${complete ? "text-emerald-700" : "text-amber-700"}`}>
        Rx print
      </span>
      <span className="flex-1 uppercase font-mono">
        <b>{index + 1}.</b>{" "}
        <b>{name || "—"}</b>
        {dose && <> · {dose}</>}
        {food && <> · {food}</>}
        {duration && <> · {duration}</>}
      </span>
      {!complete && (
        <span className="text-[10px] text-amber-800 whitespace-nowrap">missing: {missing.join(", ")}</span>
      )}
    </div>
  );
}
